import { memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@air/utils-tailwind';

import {
  WorkspaceAgentAirMark,
  type WorkspaceAgentAirMarkHandle,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentAirMark';

export type WorkspaceAgentMenuItemHoverPrefixProps = {
  className?: string;
  icon: ReactNode;
};

const resolveMenuSurfaceTheme = (element: HTMLElement | null): 'dark' | 'light' =>
  element?.closest('[data-theme="dark"]') ? 'dark' : 'light';

export const WorkspaceAgentMenuItemHoverPrefix = memo(({ className, icon }: WorkspaceAgentMenuItemHoverPrefixProps) => {
  const markRef = useRef<WorkspaceAgentAirMarkHandle>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  const [surfaceTheme, setSurfaceTheme] = useState<'dark' | 'light'>('light');

  const syncSurfaceTheme = useCallback(() => {
    setSurfaceTheme(resolveMenuSurfaceTheme(rootRef.current));
  }, []);

  const replayMark = useCallback(() => {
    syncSurfaceTheme();
    markRef.current?.replay();
  }, [syncSurfaceTheme]);

  useEffect(() => {
    syncSurfaceTheme();
  }, [syncSurfaceTheme]);

  useEffect(() => {
    const menuItem = rootRef.current?.closest('[role="menuitem"]');

    if (!menuItem) {
      return;
    }

    menuItem.addEventListener('pointerenter', replayMark);

    return () => menuItem.removeEventListener('pointerenter', replayMark);
  }, [replayMark]);

  return (
    <span className={cn('relative size-4 shrink-0', className)} ref={rootRef}>
      <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity group-hover/dropdown-menu-item:opacity-0 group-focus-visible/dropdown-menu-item:opacity-0 group-data-[highlighted]/dropdown-menu-item:opacity-0">
        {icon}
      </span>
      <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/dropdown-menu-item:opacity-100 group-focus-visible/dropdown-menu-item:opacity-100 group-data-[highlighted]/dropdown-menu-item:opacity-100">
        <WorkspaceAgentAirMark ref={markRef} surfaceTheme={surfaceTheme} />
      </span>
    </span>
  );
});

WorkspaceAgentMenuItemHoverPrefix.displayName = 'WorkspaceAgentMenuItemHoverPrefix';
